import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { encodeFunctionData } from 'viem';
import {
    useEstimateGas,
    useWriteContract,
    useWaitForTransactionReceipt,
    useSwitchChain,
} from 'wagmi';
import { toast } from 'react-toastify';
import useChainId from './useChainId';

const useContractInteraction = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [transactionHash, setTransactionHash] = useState(null);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [gasEstDetails, setGasEstDetails] = useState(null);
    const [writeContractDetails, setWriteContractDetails] = useState(null);
    const chainId = useChainId();
    const { isPending: isSwitchChainLoading, switchChainAsync } =
        useSwitchChain();

    const {
        writeContract,
        data: txHash,
        isLoading: isWriteLoading,
        error: writeError,
    } = useWriteContract();

    const {
        isLoading: isTxLoading,
        isError: isTxError,
        isSuccess: isTxSuccess,
        error: txError,
    } = useWaitForTransactionReceipt({
        confirmations: 6,
        hash: txHash,
        query: {
            enabled: !!writeContractDetails, // Ensure hooks act only for active sessions
        },
    });

    const {
        data: estimatedGas,
        error: gasError,
        isLoading: isEstGasLoading,
        isSuccess: isEstGasSuccess,
    } = useEstimateGas({
        to: gasEstDetails?.to,
        data: gasEstDetails?.data,
        chainId: gasEstDetails?.chainId,
        account: gasEstDetails?.account,
        value: gasEstDetails?.value,
        query: {
            enabled: !!gasEstDetails, // Ensure hooks act only for active sessions
        },
    });

    const checkNetwork = async (selectedChainId) => {
        if (Number(chainId) !== Number(selectedChainId)) {
            try {
                await switchChainAsync({
                    chainId: Number(selectedChainId),
                });
                return true;
            } catch (error) {
                return false;
            }
        } else {
            return true;
        }
    };

    const interact = async (
        contractAddress,
        abi,
        functionName,
        args = [],
        chainId,
        account,
        value = '0'
    ) => {
        if (await checkNetwork(chainId)) {
           
            const newSessionId = Date.now(); // Unique session ID
            setIsLoading(true); // Start loading

            const encodedFunctionData = encodeFunctionData({
                abi,
                functionName,
                args,
            });

            setGasEstDetails({
                to: contractAddress,
                chainId: Number(chainId),
                data: encodedFunctionData,
                value: ethers.parseEther(value),
                account,
            }); // Set gas estimate details

            setWriteContractDetails({
                address: contractAddress,
                abi,
                functionName,
                args,
                account,
                chainId: Number(chainId),
                value: ethers.parseEther(value),
            }); // Set write contract details
        }
    };

    useEffect(() => {
        if (estimatedGas) {
            const adjustedGas = Math.floor(Number(estimatedGas) * 1.0125);
            // Ensure this is the active session
            writeContract({
                ...writeContractDetails,
                gas: adjustedGas,
            });
        }

        if (gasError) {
          
            console.error('Transaction Error:', gasError);
            toast.error(
                gasError?.details ||
                    'An error occurred during transaction processing.'
            );
            setIsLoading(false);
            setGasEstDetails(null);
            setWriteContractDetails(null);
            // setError(txError || writeError || gasError);
        }
    }, [estimatedGas, gasError]);

    useEffect(() => {
        if (writeError) {
        
            toast.error(
                writeError?.details ||
                    'An error occurred during transaction processing.'
            );
            setIsLoading(false);
            setGasEstDetails(null);
            setWriteContractDetails(null);
            // setError(txError || writeError || gasError);
        }
    }, [writeError]);

    useEffect(() => {
        if (isTxSuccess) {
            setIsLoading(false);
            setError(null);
            setData(txHash);
            setWriteContractDetails(null);
            return;
        }

        if (isTxError) {
            setIsLoading(false);
            setWriteContractDetails(null);
            // setError(txError);
            toast.error(
                txError?.details ||
                    'An error occurred during transaction processing.'
            );
        }
    }, [isTxSuccess, isTxError]);

    return {
        interact,
        isLoading:
            isLoading ||
            isWriteLoading ||
            isTxLoading ||
            isEstGasLoading ||
            isSwitchChainLoading,
        transactionHash,
        isSuccess: isTxSuccess,
        // error,
        data,
    };
};

export default useContractInteraction;
